import { useMemo } from 'react'
import type { FieldValues, UseFormReturn } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { cn } from '@/lib/utils'

const SEGMENT_COUNT = 6

interface InputStrengthMeterProps<T extends FieldValues> {
  form: UseFormReturn<T>
  fieldsToCount: (keyof T)[]
  hasSocialMediaData?: boolean
  variant?: 'segmented' | 'continuous'
}

export const InputStrengthMeter = <T extends FieldValues>({
  form,
  fieldsToCount,
  hasSocialMediaData = false,
  variant = 'segmented',
}: InputStrengthMeterProps<T>) => {
  const { t } = useTranslation()
  const formValues = form.watch()
  const { errors } = form.formState

  const completePercentage = useMemo(() => {
    const values = form.getValues()
    const socialBonus = hasSocialMediaData ? 1 : 0
    const totalFields = fieldsToCount.length + socialBonus
    if (totalFields === 0) return 0
    const completedFields = fieldsToCount.filter((field) => {
      const value = (values as Record<string, unknown>)[field as string]
      const hasValue = value !== '' && value !== null && value !== undefined
      return hasValue && !(errors as Record<string, unknown>)[field as string]
    })
    const numberOfCompletedFields = completedFields.length + socialBonus
    return (numberOfCompletedFields / totalFields) * 100
  }, [form, fieldsToCount, hasSocialMediaData, formValues, errors])

  if (variant === 'continuous') {
    return (
      <div className="relative h-5 w-full overflow-hidden rounded-full bg-neutralColor30">
        <div
          className="h-full rounded-full bg-primaryColor transition-[width] duration-200"
          style={{ width: `${completePercentage}%` }}
        />
      </div>
    )
  }

  const filledSegments = Math.round((completePercentage / 100) * SEGMENT_COUNT)

  return (
    <div className="flex flex-col gap-3 rounded-2xl pl-3 pr-4 py-3">
      <p className="text-xs font-bold text-neutralColor">
        {t('dashboard.newSearch.common.inputStrength')}
      </p>
      <div className="flex items-center gap-4">
        {Array.from({ length: SEGMENT_COUNT }).map((_, index) => (
          <div
            key={index}
            className={cn(
              'h-2 flex-1 rounded-full',
              index < filledSegments ? 'bg-neutralColor' : 'bg-neutralColor30',
            )}
          />
        ))}
      </div>
      <div className="flex items-center justify-between text-xs font-bold text-neutralColor">
        <span>0%</span>
        <span>100%</span>
      </div>
    </div>
  )
}
